import { AxiosResponse } from 'axios'
import { useState } from 'react'
import { SelectChangeEvent } from '@mui/material/Select'
import { apiCall, ApiConfig } from '../../../config/api'
import { useConfirmsAction } from '../../../hooks/useConfirmAction'
import { Filter, SubZone, User, Zone } from '../../../interfaces'
import { SortField } from '../../../interfaces/DataGrid/DataGridSettings'
import {
  IPickingRouteState,
  IPickingRouteValidationState
} from '../../../interfaces/PickingRoute/PickingRouteState'
import { usePickingAdmin } from '../context/usePickingAdmin'
import { busquedaInitialState } from '../reducer/pickingAdminReducer'
import { pickingAdminActions } from './pickingAdminActions'
import { ApiResponse } from '../../../typings/ApiResponse'
import {
  IPickingRoute,
  IPickingRouteSearch
} from '../../../interfaces/PickingRoute/PickingRoute'
import { configDateInitial } from '../../../utils/dateHelper'

export const pickingAdminBusiness = () => {
  const { setLoadingConfirm } = useConfirmsAction()
  const { state, dispatch } = usePickingAdmin()
  const {
    setChangePage,
    setChangePageSize,
    setTotalItems,
    setRows,
    setBusqueda,
    setFilters,
    setIsLoading,
    addCheckBoxArray,
    switchSnackbar,
    setUsers,
    setSubZones,
    setOpenDelete,
    setEnabledList,
    setSorters,
    setZonesByWarehouse,
    setPickingRouteWithoutStock
  } = pickingAdminActions()
  const { busqueda, page, filters, sorters, rowsPerPage, checkboxArray } = state

  const getUserList = () => {
    apiCall('GET', `${ApiConfig.UserApi}/GetList`)
      .then((response) => response.data)
      .then((data: User[]) => {
        dispatch(setUsers(data))
      })
  }

  const getWithoutStockPickingRoutes = () => {
    apiCall(
      'GET',
      `${ApiConfig.UserApi}?pageIndex=0&pageSize=5&pickingRouteStateId=3&validationStatusId=2&isIncomplete=true`
    )
      .then(
        (response: AxiosResponse<ApiResponse<IPickingRoute>>) =>
          response.data.totalItems
      )
      .then((quantity: number) => {
        dispatch(setPickingRouteWithoutStock(quantity))
      })
  }

  const getSubZones = () => {
    apiCall('GET', `${ApiConfig.SubZoneApi}/GetList?zoneId=${busqueda.zoneId}`)
      .then((response) => response.data)
      .then((data: SubZone[]) => {
        dispatch(setSubZones(data))
      })
  }

  const fetchDataGrid = () => {
    const uri: string = `${ApiConfig.PickingRouteAdminApi}?pageIndex=${page}&pageSize=${rowsPerPage}`
    const arrFilters: string[] = filters.map(
      ({ key, value }) => `${key}=${value}`
    )
    const arrSorters: string[] = sorters
      ? sorters.map((sorter) => `sorters=${JSON.stringify(sorter)}`)
      : []
    const strFilters: string =
      arrFilters.length > 0 ? `&${arrFilters.join('&')}` : ''
    const strSorters: string =
      arrSorters.length > 0 ? `&${arrSorters.join('&')}` : ''

    const finalUri: string = uri + strFilters + strSorters

    dispatch(setIsLoading(true))
    apiCall('GET', finalUri)
      .then((response) => {
        if (response.status !== 200) {
          throw new Error(`No fue posible obtener los registros.`)
        }
        return response.data
      })
      .then(({ items, totalItems }) => {
        if (items.length === 0) {
          dispatch(setChangePage(0))
        }
        dispatch(setRows(items))
        dispatch(setTotalItems(totalItems))
        dispatch(setIsLoading(false))
      })
      .catch(() => {
        dispatch(setIsLoading(false))
        dispatch(setChangePage(0))
      })
  }

  const fetchFilteredData = () => {
    dispatch(
      setFilters(
        busqueda
          ? Object.keys(busqueda)
              .filter((key) => busqueda[key] !== '')
              .map<Filter>((key) => ({
                key: key,
                value: busqueda[key].toString()
              }))
          : []
      )
    )
  }

  const fetchFilteredData2 = (busqueda: Partial<IPickingRouteSearch>) => {
    dispatch(
      setFilters(
        busqueda
          ? Object.keys(busqueda)
              .filter((key) => busqueda[key] !== undefined)
              .filter((key) => busqueda[key] !== '')
              .map<Filter>((key) => ({
                key: key,
                value: busqueda[key].toString()
              }))
          : []
      )
    )
  }

  const submitMassiveDeletePickingRoute = () => {
    setLoadingConfirm(true)
    deletePickingRoute(checkboxArray)
  }

  const submitUnitaryDeletePickingRoute = (pickingRouteId: number) => {
    const arr: number[] = []
    arr.push(pickingRouteId)
    deletePickingRoute(arr)
  }

  const deletePickingRoute = (arrPickingRouteId: number[] | any) => {
    apiCall('DELETE', `${ApiConfig.PickingRouteApi}`, arrPickingRouteId)
      .then(async (response) => {
        switch (response.status) {
          case 204: {
            return
          }
          case 207: {
            return response.data
          }
          case 400: {
            const errors = await response.data
            throw new Error(errors.error)
          }
          default: {
            throw new Error('Hubo un error al eliminar ruta de picking.')
          }
        }
      })
      .then((data) => {
        MostrarMensajeConfirmacion(
          data !== undefined
            ? data.message
            : 'Los registros fueron eliminados correctamente',
          'success'
        )
        fetchFilteredData()
      })
      .catch(({ data: { error } }) => {
        MostrarMensajeConfirmacion(error.toString(), 'error')
      })
      .then(() => {
        dispatch({ type: 'ADD_CHECKBOX_ARRAY', payload: [] })
        setLoadingConfirm(false)
      })
  }

  const MostrarMensajeConfirmacion = (mensaje: string, tipoMensaje: any) => {
    dispatch({ type: 'SET_MESSAGE_STATUS', payload: mensaje })
    dispatch({ type: 'SET_MESSAGE_COLOR', payload: tipoMensaje })
    dispatch({ type: 'SWITCH_SNACKBAR', payload: true })
    dispatch(setOpenDelete(false))
  }

  // Setea en el reducer el valor de los fltros de busqueda
  const handleBusqueda = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      setBusqueda({ ...busqueda, [event.target.name]: event.target.value })
    )
  }

  const setWithoutStockSearchFilters = () => {
    const filters = {
      initialDate: configDateInitial((new Date().getFullYear() - 2020) * 365),
      endDate: configDateInitial(0),
      pickingRouteStateId: 3,
      validationStatusId: 2,
      isIncomplete: 'true'
    }
    dispatch(setBusqueda({ ...busqueda, ...filters }))

    fetchFilteredData2(filters)
  }

  const handleResetBusqueda = () => {
    dispatch(setBusqueda(busquedaInitialState))
    dispatch(setEnabledList(true))
  }

  const handleSnackbar = (isOpen: boolean) => {
    dispatch(switchSnackbar(isOpen))
  }

  const handlePageChange = (updatePage: number) => {
    dispatch(setChangePage(updatePage - 1))
  }

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    dispatch(setChangePage(0))
    dispatch(setChangePageSize(Number(event.target.value)))
  }

  const handleChecked = (id: number, e: any) => {
    if (e.target.checked === true) {
      dispatch(addCheckBoxArray([...checkboxArray, id]))
    } else {
      const elementsWithoutChecked = checkboxArray.filter((item) => item !== id)
      dispatch(addCheckBoxArray(elementsWithoutChecked))
    }
  }

  const handleOnSortChanged = (sorters: SortField[]) => {
    dispatch(setSorters(sorters))
  }

  const handleAssignUserPicker = (
    pickingRouteId: number,
    userPickerId: number
  ): Promise<AxiosResponse<any>> => {
    const response = apiCall(
      'POST',
      `${ApiConfig.PickingRouteApi}/ChangeRouteUser`,
      {
        pickingRouteId: pickingRouteId,
        userId: userPickerId
      }
    )
    response.then((x) => {
      if (x.data === '') {
        setTimeout(() => {
          fetchFilteredData()
        }, 2000)
      }
    })

    return response
  }

  const handleSelectedWarehouseZones = (zones: Zone[]) => {
    if (busqueda.warehouseId > 0) {
      const initialZones = [...zones!]

      const filteredZones = initialZones.filter(
        (x: Zone) => x.warehouseId === busqueda.warehouseId
      )

      dispatch(setZonesByWarehouse(filteredZones))

      if (
        filteredZones.filter((x) => x.zoneId === busqueda.zoneId).length === 0
      ) {
        dispatch(setBusqueda({ ...busqueda, zoneId: -1 }))
      }
    } else {
      dispatch(setBusqueda({ ...busqueda, warehouseId: -1, zoneId: -1 }))
    }
  }

  const [pickingRouteStates] = useState<IPickingRouteState[]>([
    {
      value: 1,
      name: 'No Iniciada'
    },
    {
      value: 2,
      name: 'Iniciada'
    },
    {
      value: 3,
      name: 'Terminada'
    }
  ])

  const [pickingRouteValidationState] = useState<
    IPickingRouteValidationState[]
  >([
    {
      value: 3,
      name: 'Validada'
    },
    {
      value: 2,
      name: 'No Validada'
    }
  ])

  return {
    getUserList,
    getSubZones,
    fetchDataGrid,
    fetchFilteredData,
    submitMassiveDeletePickingRoute,
    submitUnitaryDeletePickingRoute,
    handleBusqueda,
    handleResetBusqueda,
    handleSnackbar,
    handlePageChange,
    handlePageSizeChange,
    handleChecked,
    handleOnSortChanged,
    handleAssignUserPicker,
    handleSelectedWarehouseZones,
    pickingRouteStates,
    pickingRouteValidationState,
    getWithoutStockPickingRoutes,
    setWithoutStockSearchFilters,
    fetchFilteredData2,
    MostrarMensajeConfirmacion
  }
}
